import { Button, Grid } from "@mui/material";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { useCallback, useMemo } from "react";
import { CustomFieldsV2 } from "./CustomFieldsV2";

interface ICustomReportFieldsProps {
  windowId: string;
  customSchema: string; // the schema to validate the field values
  onSubmit?: (values: any) => void;
  isReport: boolean; // Boolean indicating whether this is a report of a file. (based on available ReportType);
}

type CustomForm = FormikProps<any>;

export const CustomReportFields = (props: ICustomReportFieldsProps): JSX.Element => {
  const { windowId, onSubmit, customSchema, isReport } = props;

  const schema = useMemo(() => (customSchema ? JSON.parse(customSchema) : null), [customSchema]);

  const mosaicPane = useMemo(() => {
    return document.getElementById(`${windowId}-panel`);
  }, [windowId]);

  const initialCustomValues = useMemo(() => {
    const values: { [key: string]: any } = {};
    if (!schema || !schema.hasOwnProperty("properties")) {
      return values;
    }
    Object.keys(schema.properties).forEach((value: string) => {
      values[value] = schema.properties[value].default ?? schema.properties[value]?.selectorProps?.defaultValue;
    });
    return values;
  }, [schema]);

  const handleSubmit = (values: any, helpers: FormikHelpers<any>): void => {
    const { resetForm } = helpers;
    if (onSubmit && typeof onSubmit === "function") {
      onSubmit(values);
      resetForm({ values });
    }
  };

  // Validate required fields defined in the schema.
  const validate = useCallback(
    (values: any) => {
      const errors: { [key: string]: string } = {};
      if (!schema?.required?.length) return errors;
      for (const field of schema.required as string[]) {
        const value = values[field];
        const isNullish = value == null || value === "";
        const isEmptyArray = Array.isArray(value) && value.length === 0;
        if (isNullish || isEmptyArray) {
          errors[field] = "required";
        }
      }
      return errors;
    },
    [schema]
  );

  if (!schema) {
    return <span>loading...</span>;
  }

  return (
    <div>
      <Formik
        initialValues={initialCustomValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        validate={validate}
        validateOnMount={true}
      >
        {(customForm: CustomForm) => {
          const { dirty, isValid } = customForm;

          return (
            <Form>
              <Grid container justifyContent="flex-start">
                <CustomFieldsV2 mosaicPane={mosaicPane} schema={schema} labelAlignment={"left"} />
                <Grid container style={{ margin: "10px 0px" }}>
                  <Button
                    className="resetfields_btn"
                    type="reset"
                    disabled={!dirty}
                    variant="contained"
                    color="secondary"
                  >
                    Reset
                  </Button>
                  <Button
                    className="openreport_btn"
                    type="submit"
                    disabled={!isValid}
                    variant="contained"
                    color="primary"
                  >
                    {isReport ? "Open Report" : "Generate File"}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
